import React from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_CLASS_BY_ID,
  GET_CLASS_CANCELLATION_REQUESTS,
  ME,
  GET_STUDENT_LIST,
} from "../graphql/query";
import {
  APPROVE_CLASS_CANCELLATION_REQUEST,
  CREATE_CLASS_CANCELLATION_REQUEST,
  REJECT_CLASS_CANCELLATION_REQUEST,
  CREATE_OR_UPDATE_ATTENDANCE,
  UPDATE_CLASS,
} from "../graphql/mutation";
import {
  GetClassCancellationRequestsResponse,
  StudentAttendance,
} from "../interfaces/attendance.interface";
import themestyle from "../theme";
import AttendanceDataGrid from "./AttendanceDataGrid";
import { formatTo12Hour } from "../utils/time";
import SearchIcon from "@mui/icons-material/Search";
import { TextField, InputAdornment } from "@mui/material";
import AttendanceReviewModel from "./AttendanceReviewModel";
import RequestCancleModel from "./RequestCancleModel";
import AddAttendanceModel from "./AddAttendanceModel";
import { CLASS_TYPE_UI } from "../utils/const";
import CustomSnackbar from "./Snackbar";

interface Props {
  classId?: string;
  sessionDate?: string; // specific session date from calendar navigation
  onBack: () => void;
}

const ClassAttendance: React.FC<Props> = ({ classId, sessionDate, onBack }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchQuery, setSearchQuery] = React.useState("");
  const [localStudents, setLocalStudents] = React.useState<StudentAttendance[]>(
    [],
  );
  const [isReviewOpen, setIsReviewOpen] = React.useState(false);
  const [isCancelOpen, setIsCancelOpen] = React.useState(false);
  const [isAddAttendanceOpen, setIsAddAttendanceOpen] = React.useState(false);
  const [isSuccess, setIsSuccess] = React.useState(false);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState("");
  const [snackbarType, setSnackbarType] = React.useState<
    "success" | "error" | "info"
  >("success");

  const { data, loading } = useQuery(GET_CLASS_BY_ID, {
    variables: { id: classId },
    skip: !classId,
  });

  const { data: userData } = useQuery(ME, {
    fetchPolicy: "cache-first",
  });

  const userRole =
    userData?.me?.role?.toLowerCase() ||
    JSON.parse(localStorage.getItem("user") || "{}")?.role?.toLowerCase() ||
    "";
  const canReviewCancellation =
    userRole === "admin" || userRole === "super_admin";

  const [createOrUpdateAttendance, { loading: savingAttendance }] = useMutation(
    CREATE_OR_UPDATE_ATTENDANCE,
  );
  const [updateClass] = useMutation(UPDATE_CLASS);
  const [createCancellationRequest] = useMutation(
    CREATE_CLASS_CANCELLATION_REQUEST,
  );
  const [approveCancellationRequest, { loading: approvingCancellation }] =
    useMutation(APPROVE_CLASS_CANCELLATION_REQUEST);
  const [rejectCancellationRequest, { loading: rejectingCancellation }] =
    useMutation(REJECT_CLASS_CANCELLATION_REQUEST);

  const { data: cancellationData, refetch: refetchCancellations } =
    useQuery<GetClassCancellationRequestsResponse>(
    GET_CLASS_CANCELLATION_REQUESTS,
    {
      fetchPolicy: "network-only",
    },
  );
  const { data: allStudentsData } = useQuery(GET_STUDENT_LIST, {
    variables: { page: 1, perPage: 1000, search: "" },
    fetchPolicy: "network-only",
  });

  const handleStudentsChange = (updatedStudents: StudentAttendance[]) => {
    setLocalStudents(updatedStudents);
  };
  const handleEnrollStudent = async (data: {
    studentId: string;
    category: string;
  }) => {
    const student = allStudentsData?.getStudentList?.data?.find(
      (s: any) => s.studentId === data.studentId,
    );
    if (!student) return;
    const studentExists = localStudents.find((s) => s.studentId === student.id);
    if (studentExists) {
      setSnackbarMessage("This student is already enrolled in this class");
      setSnackbarType("error");
      setSnackbarOpen(true);
      return;
    }
    const newStudent: StudentAttendance = {
      studentId: student.id,
      studentName: student.fullName,
      isPresent: true,
      sessionCategory: data.category,
    } as any;

    const updatedStudents = [...localStudents, newStudent];
    setLocalStudents(updatedStudents);
    try {
      const attendanceDate = sessionDate ?? classData?.day;
      const { data: response } = await createOrUpdateAttendance({
        variables: {
          input: {
            classId: classId,
            date: attendanceDate,
            students: updatedStudents.map((s) => ({
              studentId: s.studentId,
              studentName: s.studentName,
              isPresent: s.isPresent,
              sessionCategory: (s as any).sessionCategory ?? null,
            })),
          },
        },
      });
      if (response?.createOrUpdateAttendance?.success) {
        setSnackbarMessage(
          `Student added and marked present for this session (${data.category})`,
        );
        setSnackbarType("success");
        setIsAddAttendanceOpen(false);
      } else {
        setSnackbarMessage(
          response?.createOrUpdateAttendance?.message ||
            "Failed to save attendance",
        );
        setSnackbarType("error");
      }
      setSnackbarOpen(true);
    } catch (error) {
      console.error(error);
    }
  };
  const handleSaveAttendance = () => {
    if (!classId) return;
    if (classData?.isCancelled) {
      setSnackbarMessage(
        "This class is cancelled. Attendance cannot be edited.",
      );
      setSnackbarType("error");
      setSnackbarOpen(true);
      return;
    }
    setIsSuccess(false);
    setIsReviewOpen(true);
  };

  const handleConfirmSave = async () => {
    if (!classId) return;
    if (classData?.isCancelled) {
      setSnackbarMessage(
        "This class is cancelled. Attendance cannot be edited.",
      );
      setSnackbarType("error");
      setSnackbarOpen(true);
      return;
    }
    try {
      // Use sessionDate from navigation state if available; fallback to classData.day
      const attendanceDate = sessionDate ?? classData?.day;
      const { data: response } = await createOrUpdateAttendance({
        variables: {
          input: {
            classId: classId,
            date: attendanceDate,
            students: localStudents.map((s) => ({
              studentId: s.studentId,
              studentName: s.studentName,
              isPresent: s.isPresent,
              sessionCategory: (s as any).sessionCategory ?? null,
            })),
          },
        },
      });
      const success = response?.createOrUpdateAttendance?.success;
      if (success) {
        setIsSuccess(true);
      } else {
        setSnackbarMessage(
          response?.createOrUpdateAttendance?.message ||
            "Failed to save attendance",
        );
        setSnackbarType("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error(error);
      setSnackbarMessage("Failed to save attendance");
      setSnackbarType("error");
      setSnackbarOpen(true);
    }
  };

  const handleSendCancellationRequest = async (reason: string) => {
    if (!classId) {
      setSnackbarMessage("Class not found");
      setSnackbarType("error");
      setSnackbarOpen(true);
      return;
    }
    try {
      const { data: response } = await createCancellationRequest({
        variables: {
          input: {
            classId,
            reason,
            sessionDate: displayDate,
          },
        },
      });
      const success = response?.createClassCancellationRequest?.success;
      setSnackbarMessage(
        response?.createClassCancellationRequest?.message ||
          (success ? "Request sent" : "Failed to send request"),
      );
      setSnackbarType(success ? "success" : "error");
      setSnackbarOpen(true);
      if (success) {
        refetchCancellations();
      }
    } catch (error: any) {
      setSnackbarMessage(error?.message || "Failed to send request");
      setSnackbarType("error");
      setSnackbarOpen(true);
    }
  };

  const handleReviewCancellation = async (
    requestId: string,
    decision: "approve" | "reject",
  ) => {
    try {
      const { data: response } = await (decision === "approve"
        ? approveCancellationRequest({
            variables: { id: requestId },
          })
        : rejectCancellationRequest({
            variables: { id: requestId },
          }));
      const result =
        decision === "approve"
          ? response?.approveClassCancellationRequest
          : response?.rejectClassCancellationRequest;
      const success = result?.success;
      setSnackbarMessage(
        result?.message ||
          (success
            ? decision === "approve"
              ? "Cancellation approved"
              : "Cancellation rejected"
            : decision === "approve"
              ? "Failed to approve cancellation"
              : "Failed to reject cancellation"),
      );
      setSnackbarType(success ? "success" : "error");
      setSnackbarOpen(true);
      if (success) {
        refetchCancellations();
      }
    } catch (error: any) {
      setSnackbarMessage(
        error?.message ||
          (decision === "approve"
            ? "Failed to approve cancellation"
            : "Failed to reject cancellation"),
      );
      setSnackbarType("error");
      setSnackbarOpen(true);
    }
  };
  const classData = data?.getClassById?.data;
  const displayDate = sessionDate ?? classData?.day ?? "";
  const isPastClass = React.useMemo(() => {
    const dateForCheck = sessionDate ?? classData?.day;
    if (!dateForCheck || !classData?.endTime) return false;
    const classEndDateTime = new Date(
      `${dateForCheck}T${classData.endTime}:00`,
    );
    return new Date() > classEndDateTime;
  }, [sessionDate, classData?.day, classData?.endTime]);

  const isClassCancelled = React.useMemo(() => {
    if (!classData) return false;
    return Boolean(classData.isCancelled) || 
           (Array.isArray(classData.cancelledDates) && classData.cancelledDates.includes(displayDate));
  }, [classData, displayDate]);

  const isInstructor = userRole === "instructor";
  const shouldDisableEditing = isClassCancelled && isInstructor;
  const pendingRequestsForThisClass = (
    cancellationData?.getClassCancellationRequests?.data || []
  ).filter(
    (request) => request.status === "PENDING" && request.classId === classId,
  );
  const getDayName = (dateString: string) => {
    if (!dateString || !dateString.includes("-")) return dateString || "";
    const date = new Date(dateString + "T00:00:00");
    if (isNaN(date.getTime())) return dateString;
    return new Intl.DateTimeFormat("en-US", { weekday: "short" }).format(date);
  };

  const formatDate = (dateString: string) => {
    if (!dateString || !dateString.includes("-")) return dateString || "";
    const [y, m, d] = dateString.split("-");
    return `${d}.${m}.${y}`;
  };

  const currentSessionRequest = React.useMemo(() => {
    return (
      cancellationData?.getClassCancellationRequests?.data || []
    ).find(
      (req) => req.classId === classId && req.sessionDate === displayDate,
    );
  }, [cancellationData, classId, displayDate]);
  const canRequestCancellation = 
    userRole === "instructor";
  const reviewingCancellation =
    approvingCancellation || rejectingCancellation;
  const renderCancellationStatus = () => {
    if (!canRequestCancellation || isClassCancelled) return null;
    if (currentSessionRequest) {
      const isPending = currentSessionRequest.status === "PENDING";
      const isApproved = currentSessionRequest.status === "APPROVED";
      const isRejected = currentSessionRequest.status === "REJECTED";
      if (isPending || isApproved || isRejected) {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: isPending
                ? "linear-gradient(135deg, #FFFDF3 0%, #FFF7E8 100%)"
                : isApproved
                  ? "linear-gradient(135deg, #F4FFF8 0%, #EAFBF0 100%)"
                  : "linear-gradient(135deg, #FFF5F5 0%, #FEECEC 100%)",
              border: `1px solid ${isPending ? "#FDE68A" : isApproved ? "#BBF7D0" : "#FECACA"}`,
              boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
              borderRadius: "14px",
              px: isMobile ? 1.75 : 2.25,
              py: isMobile ? 1.1 : 1.15,
              minWidth: isMobile ? "160px" : "230px",
              minHeight: isMobile ? "64px" : "auto",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              {isPending ? (
                <HourglassTopIcon sx={{ color: "#D97706", fontSize: 20 }} />
              ) : isApproved ? (
                <CheckCircleOutlineIcon sx={{ color: "#16A34A", fontSize: 20 }} />
              ) : (
                <ErrorOutlineIcon sx={{ color: "#DC2626", fontSize: 20 }} />
              )}
              <Box sx={{ textAlign: "left" }}>
                <Typography
                  sx={{
                    color: isPending ? "#B45309" : isApproved ? "#15803D" : "#B91C1C",
                    fontWeight: 800,
                    fontSize: isMobile ? "12px" : "14px",
                    lineHeight: 1.1,
                  }}
                >
                  {isPending ? "Approval Pending" : isApproved ? "Approved" : "Rejected"}
                </Typography>
                <Typography
                  sx={{
                    color: isPending ? "#A16207" : isApproved ? "#166534" : "#991B1B",
                    fontWeight: 500,
                    fontSize: isMobile ? "10px" : "11px",
                    lineHeight: 1.1,
                    mt: 0.35,
                  }}
                >
                  {isPending
                    ? "Waiting for admin review"
                    : isApproved
                      ? "Cancellation approved"
                      : "Cancellation rejected"}
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      }
    }

    return (
      <Button
        variant="outlined"
        startIcon={isMobile ? undefined : <ErrorOutlineIcon />}
        onClick={() => setIsCancelOpen(true)}
        sx={{
          flex: isMobile ? 1 : "none",
          color: "#EF4444",
          borderColor: "#FEE2E2",
          backgroundColor: themestyle.colors.white,
          textTransform: "none",
          fontWeight: 600,
          borderRadius: "8px",
          px: isMobile ? 0.5 : 2.5,
          py: isMobile ? 1 : 1.2,
          fontSize: isMobile ? "11px" : "14px",
          width: isMobile ? "100%" : "auto",
          minHeight: isMobile ? "64px" : "auto",
          textAlign: "center",
          whiteSpace: isMobile ? "normal" : "nowrap",
          justifyContent: "center",
          "&:hover": {
            borderColor: "#EF4444",
            backgroundColor: "#FEF2F2",
          },
        }}
      >
        {isMobile ? (
          <>
            Request
            <br />
            Cancellation
          </>
        ) : (
          "Request Cancellation"
        )}
      </Button>
    );
  };
  return (
    <Box sx={{ p: isMobile ? 1 : 2 }}>
      <Box
        sx={{
          backgroundColor: themestyle.colors.white,
          borderRadius: "12px",
          border: "1px solid #E5E7EB",
          p: isMobile ? 2 : 3,
          mb: 3,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            fontWeight={600}
            sx={{ fontSize: isMobile ? "18px" : "22px", mb: 0.5 }}
          >
            {classData?.className || "Loading..."}
          </Typography>
          <Typography sx={{ color: "#6B7280", fontSize: "14px", mb: 0.5 }}>
            {classData
              ? `${formatDate(displayDate)}, ${getDayName(displayDate)}, ${formatTo12Hour(classData.startTime)} - ${formatTo12Hour(classData.endTime)}`
              : ""}
          </Typography>
          <Typography sx={{ color: "#6B7280", fontSize: "14px", mb: 0.5 }}>
            Instructor: {classData?.instructorName || ""}
          </Typography>
          <Typography sx={{ color: "#6B7280", fontSize: "14px" }}>
            {classData?.classType
              ? CLASS_TYPE_UI[classData.classType]?.label || classData.classType
              : ""}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: isMobile ? 1 : 1.5,
            width: isMobile ? "100%" : "auto",
            justifyContent: isMobile ? "flex-start" : "flex-end",
            alignItems: "center",
          }}
        >
          {renderCancellationStatus()}
          {userRole !== "instructor" && (
            <Button
              variant="outlined"
              onClick={() => setIsAddAttendanceOpen(true)}
              disabled={shouldDisableEditing}
              sx={{
                flex: isMobile ? 1 : "none",
                color: "#374151",
                borderColor: "#E5E7EB",
                backgroundColor: themestyle.colors.white,
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                px: isMobile ? 0.5 : 2.5,
                py: isMobile ? 1 : 1.2,
                fontSize: isMobile ? "11px" : "14px",
                width: isMobile ? "100%" : "auto",
                minHeight: isMobile ? "64px" : "auto",
                textAlign: "center",
                "&:hover": {
                  borderColor: "#9CA3AF",
                  backgroundColor: "#F9FAFB",
                },
              }}
            >
              {isMobile ? (
                <>
                  Add
                  <br />
                  Attendance
                </>
              ) : (
                "Add Attendance"
              )}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleSaveAttendance}
            disabled={savingAttendance || shouldDisableEditing}
            sx={{
              flex: isMobile ? 1 : "none",
              backgroundColor: themestyle.colors.activeGreen,
              color: themestyle.colors.white,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              px: isMobile ? 0.5 : 2.5,
              py: isMobile ? 1 : 1.2,
              fontSize: isMobile ? "11px" : "14px",
              width: isMobile ? "100%" : "auto",
              minHeight: isMobile ? "64px" : "auto",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#65A30D",
                boxShadow: "none",
              },
            }}
          >
            {isMobile ? (
              <>
                Save
                <br />
                Attendance
              </>
            ) : (
              "Save Attendance"
            )}
          </Button>
        </Box>
      </Box>
      {isClassCancelled && (
        <Box
          sx={{
            backgroundColor: "#FEF2F2",
            border: "1px solid #FECACA",
            color: "#B91C1C",
            borderRadius: "10px",
            px: 2,
            py: 1.2,
            mb: 2,
            fontSize: isMobile ? "12px" : "14px",
            fontWeight: 600,
          }}
        >
          This class has been cancelled. {isInstructor ? "Attendance changes are disabled." : "You can still edit attendance as an admin."}
        </Box>
      )}
      {canReviewCancellation && (
        <Box
          sx={{
            backgroundColor: themestyle.colors.white,
            borderRadius: "12px",
            border: "1px solid #E5E7EB",
            p: isMobile ? 2 : 2.5,
            mb: 3,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: isMobile ? "14px" : "16px",
              mb: 1.2,
            }}
          >
            Cancellation Requests For This Class
          </Typography>
          {pendingRequestsForThisClass.length === 0 ? (
            <Typography sx={{ color: "#6B7280", fontSize: "13px" }}>
              No pending cancellation requests for this class.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {pendingRequestsForThisClass.map((request) => (
                <Box
                  key={request.id}
                  sx={{
                    border: "1px solid #F3E8E8",
                    backgroundColor: "#FFF7F7",
                    borderRadius: "10px",
                    p: isMobile ? 1.2 : 1.6,
                  }}
                >
                  <Typography
                    sx={{ color: "#111827", fontSize: "14px", mb: 0.8 }}
                  >
                    {request.reason}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1,
                      flexDirection: isMobile ? "column" : "row",
                    }}
                  >
                    <Typography sx={{ color: "#6B7280", fontSize: "12px" }}>
                      Requested by {request.requestedByName}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, width: isMobile ? "100%" : "auto" }}>
                      <Button
                        variant="outlined"
                        onClick={() => handleReviewCancellation(request.id, "reject")}
                        disabled={reviewingCancellation}
                        sx={{
                          textTransform: "none",
                          borderRadius: "8px",
                          color: "#B91C1C",
                          borderColor: "#FCA5A5",
                          width: isMobile ? "50%" : "auto",
                          "&:hover": {
                            borderColor: "#EF4444",
                            backgroundColor: "#FEF2F2",
                          },
                        }}
                      >
                        Reject
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => handleReviewCancellation(request.id, "approve")}
                        disabled={reviewingCancellation}
                        sx={{
                          textTransform: "none",
                          borderRadius: "8px",
                          width: isMobile ? "50%" : "auto",
                          backgroundColor: themestyle.colors.activeGreen,
                          "&:hover": {
                            backgroundColor: themestyle.colors.activeGreen,
                          },
                        }}
                      >
                        Approve Cancellation
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
      <Box
        sx={{
          backgroundColor: themestyle.colors.white,
          borderRadius: "12px",
          border: "1px solid #E5E7EB",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "stretch" : "center",
            backgroundColor: "#F9FAFB",
            borderBottom: "1px solid #E5E7EB",
            p: 2,
            px: isMobile ? 2 : 3,
            gap: isMobile ? 1.5 : 0,
          }}
        >
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ fontSize: isMobile ? "14px" : "18px", color: "#111827" }}
          >
            Enrolled Students ({localStudents.length}/30)
          </Typography>
          <TextField
            size="small"
            placeholder="Search student..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              width: isMobile ? "100%" : 250,
              "& .MuiOutlinedInput-root": {
                borderRadius: "50px",
                backgroundColor: themestyle.colors.white,
                fontSize: "14px",
                height: isMobile ? "36px" : "38px",
                "& fieldset": { borderColor: "#E5E7EB" },
                "&:hover fieldset": { borderColor: "#D1D5DB" },
                "&.Mui-focused fieldset": { borderColor: "#84CC16" },
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#9CA3AF",
                opacity: 1,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#9CA3AF", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <AttendanceDataGrid
          classId={classId}
          attendanceDate={sessionDate ?? classData?.day}
          loading={loading}
          searchQuery={searchQuery}
          onStudentsChange={handleStudentsChange}
          readOnly={shouldDisableEditing}
          defaultStudentLabel={
            classData?.classType
              ? CLASS_TYPE_UI[classData.classType]?.label || classData.classType
              : "Enrolled"
          }
          externalStudents={
            localStudents.length > 0 ? localStudents : undefined
          }
        />
      </Box>
      <AttendanceReviewModel
        open={isReviewOpen}
        onClose={() => setIsReviewOpen(false)}
        onConfirm={handleConfirmSave}
        onReturn={onBack}
        totalStudents={localStudents.length}
        presentCount={localStudents.filter((s) => s.isPresent).length}
        walkInCount={localStudents.filter((s) => !!(s as any).sessionCategory).length}
        loading={savingAttendance}
        isSuccess={isSuccess}
      />
      <RequestCancleModel
        open={isCancelOpen}
        onClose={() => setIsCancelOpen(false)}
        className={classData?.className}
        onSend={handleSendCancellationRequest}
      />
      <AddAttendanceModel
        open={isAddAttendanceOpen}
        onClose={() => setIsAddAttendanceOpen(false)}
        students={(allStudentsData?.getStudentList?.data || []).map(
          (s: any) => ({
            studentId: s.studentId,
            studentName: s.fullName,
            isPresent: false,
          }),
        )}
        onEnroll={handleEnrollStudent}
        loading={savingAttendance}
      />
      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        type={snackbarType}
        onClose={() => setSnackbarOpen(false)}
      />
    </Box>
  );
};
export default ClassAttendance;
